'use client'

import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { motion, AnimatePresence } from 'framer-motion'
import LanguageSelector from './LanguageSelector'
import ThemeToggle from './ThemeToggle'

export default function Navigation() {
  const t = useTranslations('navigation')
  const [isOpen, setIsOpen] = useState(false)
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([])

  const toggleDropdown = (key: string) => {
    setOpenDropdowns(prev =>
      prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key]
    )
  }

  const navItems = [
    { key: 'about', href: '/about' },
    {
      key: 'products',
      href: '/products/aipuna',
      submenu: [
        { key: 'aipuna', href: '/products/aipuna' },
        { key: 'janus', href: '/products/janus' },
        { key: 'octochat', href: '/products/octochat' },
        { key: 'learncoach', href: '/products/learncoach' },
        { key: 'agodoc', href: '/products/agodoc' },
      ],
    },
    { key: 'services', href: '/services' },
    { key: 'contact', href: '/contact' },
    { key: 'blog', href: '/blog' },
  ]

  return (
    <nav className="bg-white dark:bg-secondary-900 shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo */}
          <Link
            href="/"
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
          >
            <div className="relative h-24 w-24 sm:h-28 sm:w-28 md:h-32 md:w-32">
              <Image
                src="/tobg-logo.png"
                alt="TOBG"
                fill
                className="object-contain rounded-lg"
                priority
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            {navItems.map((item) => (
              <div key={item.key} className="relative group">
                <Link
                  href={item.href}
                  className="text-secondary-700 dark:text-secondary-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors py-2"
                >
                  {t(item.key)}
                </Link>
                {item.submenu && (
                  <div className="absolute left-0 mt-2 w-48 bg-white dark:bg-secondary-800 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.key}
                        href={subItem.href}
                        className="block px-4 py-2 text-sm text-secondary-700 dark:text-secondary-200 hover:bg-primary-50 dark:hover:bg-secondary-700"
                      >
                        {t(subItem.key)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center space-x-2">
              <ThemeToggle />
              <LanguageSelector />
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:hidden">
            <ThemeToggle />
            <LanguageSelector />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-3 rounded-md text-secondary-700 dark:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-800 min-w-[48px] min-h-[48px] flex items-center justify-center"
            >
              <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                {isOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="py-4 space-y-2">
                {navItems.map((item) => (
                  <div key={item.key}>
                    <div className="flex items-center justify-between">
                      <Link
                        href={item.href}
                        className="block px-4 py-3 text-base text-secondary-700 dark:text-secondary-200 hover:bg-primary-50 dark:hover:bg-secondary-800 rounded-md min-h-[48px] flex items-center"
                        onClick={() => setIsOpen(false)}
                      >
                        {t(item.key)}
                      </Link>
                      {item.submenu && (
                        <button
                          onClick={() => toggleDropdown(item.key)}
                          className="px-3 py-3 text-secondary-500 min-w-[48px] min-h-[48px] flex items-center justify-center"
                        >
                          <svg
                            className={`w-4 h-4 transition-transform ${
                              openDropdowns.includes(item.key) ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {item.submenu && openDropdowns.includes(item.key) && (
                      <div className="ml-8 mt-2 space-y-1">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.key}
                            href={subItem.href}
                            className="block px-4 py-3 text-base text-secondary-600 dark:text-secondary-300 hover:bg-primary-50 dark:hover:bg-secondary-800 rounded-md min-h-[48px] flex items-center"
                            onClick={() => setIsOpen(false)}
                          >
                            {t(subItem.key)}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  )
}
